import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  dedupeNotifications,
  normaliseNotifications,
  notificationKey,
  notificationReference,
  unreadTypes,
  type NotificationApiItem,
} from '@mypoz/shared';

import { NotificationsContext, type NotificationsState } from './context.js';
import { fetchNotificationRows, markNotificationsRead, type MarkReadTarget } from './api.js';
import type { DashboardNotification } from './types.js';

function toDashboardNotification(item: NotificationApiItem): DashboardNotification {
  return { ...item, reference: notificationReference(item) };
}

/**
 * Holds real notification state for the session (MYP-112) — replaces the `NOTIFICATIONS_FIXTURE`
 * this used to seed from.
 *
 * Fetched whole rather than paginated over the network, for the reason `fetchNotificationRows`
 * documents: `get_msgs` does not sort by date, so presenting one correctly-ordered feed needs the
 * complete set before it can be sorted. The Notifications page's pager is therefore a client-side
 * slice of an already-complete, already-sorted list, not a further network request — there is
 * nothing more the vendor would send that this hasn't already got.
 *
 * Read state is saved on the vendor with `set_msgs_read` (MYP-70; the endpoint the vendor added
 * on 2026-09-28 in answer to MYP-81). It is also kept here as a set of composite keys layered over
 * the fetched rows, and that set is applied first: the row turns read the moment it is opened,
 * the request goes out behind it, and a failed request is logged rather than flipping the row
 * back — the worst case is a message that shows unread again on the next visit. The overlay also
 * means a `reload()` that lands before the vendor has saved cannot resurrect a message this
 * session already opened.
 *
 * The unread count stays counted here, from `readflag`: `get_msgs`'s own `readflag=N` filter still
 * returns read messages (checked live 2026-09-29), so it is not asked for one.
 *
 * `dedupeNotifications` still runs here for the reason MYP-66 gave it: nothing about this
 * endpoint guarantees a message can't appear twice across whatever `get_msgs` considers its own
 * pages.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [rows, setRows] = useState<readonly unknown[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [readOverrides, setReadOverrides] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    // Guards both overlapping-fetch cases at once: StrictMode's dev double-mount, and a reload
    // pressed while the previous attempt is still running. Only the newest effect's result lands.
    let current = true;

    setRows(undefined);
    setFailed(false);

    fetchNotificationRows().then(
      (nextRows) => {
        if (current) setRows(nextRows);
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown raw: the vendor's failure text is Chinese prose from its own code
        // table, useful to whoever is debugging and not something to put in front of a customer.
        console.error('[notifications] could not load the notification feed', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  /**
   * The account's messages, newest first and deduplicated — `get_msgs` hands back neither
   * property on its own (see `fetchNotificationRows`'s doc comment), so both happen here, once,
   * rather than at every place that reads this list.
   */
  const notifications = useMemo(() => {
    const normalised = normaliseNotifications(rows ?? []).map(toDashboardNotification);
    const withReadOverrides = normalised.map((item) =>
      readOverrides.has(notificationKey(item)) ? { ...item, readflag: 'Y' } : item,
    );
    const sorted = withReadOverrides
      .slice()
      .sort((a, b) => Date.parse(b.msgdate) - Date.parse(a.msgdate));

    return dedupeNotifications(sorted);
  }, [rows, readOverrides]);

  /** The current list, for the callbacks below, which must not be rebuilt on every fetch. */
  const latest = useRef(notifications);
  useEffect(() => {
    latest.current = notifications;
  }, [notifications]);

  /** Sends one `set_msgs_read`; a failure is logged only (see the comment on the provider). */
  const send = useCallback((target: MarkReadTarget) => {
    markNotificationsRead(target).catch((cause: unknown) => {
      console.error('[notifications] could not save read state', target, cause);
    });
  }, []);

  const markRead = useCallback(
    (key: string) => {
      const item = latest.current.find((entry) => notificationKey(entry) === key);
      // Only an unread message is sent: opening a read one again costs the vendor nothing.
      if (item === undefined || item.readflag !== 'N') return;
      setReadOverrides((current) => new Set(current).add(key));
      send({ tid: item.tid, type: item.type });
    },
    [send],
  );

  /**
   * Every unread message, read. `set_msgs_read` has no "everything" form, so it is one request per
   * type that still has an unread message — at most six (the six `type` codes `get_msgs` sends).
   */
  const markAllRead = useCallback(() => {
    const unread = latest.current.filter((entry) => entry.readflag === 'N');
    if (unread.length === 0) return;
    setReadOverrides((current) => {
      const next = new Set(current);
      for (const entry of unread) next.add(notificationKey(entry));
      return next;
    });
    for (const type of unreadTypes(unread)) send({ type });
  }, [send]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const value = useMemo<NotificationsState>(
    () => ({
      notifications,
      // Counted client-side from `readflag`, over the *complete* fetched set rather than
      // whichever page the Notifications page currently shows — the header bell
      // and sidebar count are meant to reflect the whole account, not one page of it.
      unreadCount: notifications.filter((item) => item.readflag === 'N').length,
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      markRead,
      markAllRead,
      reload,
    }),
    [notifications, rows, failed, markRead, markAllRead, reload],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
